import React, {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useState,
  useMemo
} from 'react'
import { useLocation } from 'react-router-dom'
import {
  storeTokensForUser,
  conditionallyClearStorage,
  getJwt
} from '../../services/user.session.service'
import { UserCredentials } from '../../domain/identity'
import { User } from '../../domain/user'
import { useApp } from '../AppContext/AppProvider'
import {
  AccountRecovery,
  AccountRecoveryRequest,
  AuthCredentials,
  AuthProviderContext
} from './types'
import { extractMessageFromServiceError } from '../../services/error.service'
import {
  onLogout as resetAmplitude,
  setIdentity as setAmplitudeIdentity
} from '../../services/amplitude.service'
import { setBranchUser, resetBranch } from '../../services/branch.service'

const authProviderContext = createContext({} as AuthProviderContext)

const AppProvider: React.FC<{ children: ReactNode }> = ({
  children
}): JSX.Element => {
  const [user, setUser] = useState<User | null>(null)
  const [userCredentials, setUserCredentials] =
    useState<UserCredentials | null>(null)
  const [authError, setError] = useState<string | null>()
  const [loading, setLoading] = useState<boolean>(false)

  const { application } = useApp()
  const location = useLocation()

  const setTrackingIds = (user: User): void => {
    setBranchUser(user.externalId)
    setAmplitudeIdentity(user)
  }

  const getUser = (): Promise<void> => {
    return application
      .service('user')
      .find()
      .then((user: User) => {
        setUser(user)
        setTrackingIds(user)
      })
  }

  /**
   * Note: while it would be ideal to rely on contact verification
   * (email/phone) on the User, unfortunately testing reveals that
   * the Congito attribute updates are not always propagated to the
   * User.
   *
   * TODO: Follow up on this while addressing support for
   * phone verification in the `auth` service?
   */
  const getCredentials = (): Promise<void> => {
    return application
      .service('auth/credentials')
      .find()
      .then(setUserCredentials)
  }

  const saveUser = (primaryEmail: string): Promise<void> => {
    return application.service('user').create({ primaryEmail })
  }

  const loadUserSession = (): Promise<void> => {
    setLoading(true)

    return getCredentials()
      .then(getUser)
      .catch((error: unknown) => {
        logout()
        setError(extractMessageFromServiceError(error))
      })
      .finally(() => setLoading(false))
  }

  const login = ({ username, password }: AuthCredentials): Promise<void> => {
    setLoading(true)
    setError(null)
    return application
      .service('auth/session')
      .create({ username, password })
      .then(storeTokensForUser)
      .then(getCredentials)
      .then(getUser)
      .catch((error: unknown) =>
        setError(extractMessageFromServiceError(error))
      )
      .finally(() => setLoading(false))
  }

  const signUp = ({ username, password }: AuthCredentials): Promise<void> => {
    setLoading(true)
    setError(null)
    return application
      .service('auth/credentials')
      .create({ username, password })
      .then(storeTokensForUser)
      .then(() => saveUser(username))
      .then(getCredentials)
      .then(getUser)
      .catch((error: unknown) =>
        setError(extractMessageFromServiceError(error))
      )
      .finally(() => setLoading(false))
  }

  const logout = (): void => {
    conditionallyClearStorage()
    setUserCredentials(null)
    setUser(null)
    resetAmplitude()
    resetBranch()
  }

  const requestResetPassword = ({
    email
  }: AccountRecoveryRequest): Promise<void> => {
    setLoading(true)
    setError(null)

    return application
      .service('auth/credentials/temporary')
      .create({ username: email })
      .catch((error: unknown) =>
        setError(extractMessageFromServiceError(error))
      )
      .finally(() => setLoading(false))
  }

  const resetPassword = ({ code, email, password }: AccountRecovery) => {
    setLoading(true)
    setError(null)

    return application
      .service('auth/credentials')
      .update(
        null,
        { username: email, password },
        { headers: { 'X-Paceline-Temp-Code': code } }
      )
      .catch((error: unknown) =>
        setError(extractMessageFromServiceError(error))
      )
      .finally(() => setLoading(false))
  }

  /**
   * If page is changed reset the error state.
   */
  useEffect(() => {
    if (authError) setError(null)
  }, [location.pathname])

  /**
   * This effect will run everytime the user object changes
   * or when a hard reload occurs
   */
  useEffect(() => {
    const jwt = getJwt()

    // attempt to reload user with current session
    if (!user && !!jwt) {
      loadUserSession()
    }
  }, [user])

  /**
   *  Make the provider update only when it should.
   *  We only want to force re-renders if the user,
   *  loading or error states change.
   */
  const memoedValue = useMemo(
    () => ({
      userCredentials,
      login,
      user,
      signUp,
      authError,
      getCredentials,
      loading,
      logout,
      requestResetPassword,
      resetPassword
    }),
    [user, loading, authError, userCredentials]
  )

  return (
    <authProviderContext.Provider value={memoedValue as AuthProviderContext}>
      {children}
    </authProviderContext.Provider>
  )
}

const useAuth = () => useContext(authProviderContext)

export { authProviderContext, useAuth }

export default AppProvider
